// src/components/Eventos/EventsTable.jsx
import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Chip,
  Avatar,
  LinearProgress,
  Skeleton,
  Alert,
  IconButton,
  useMediaQuery,
  useTheme,
  Typography,
  Stack,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Grid,
  Button,
  Collapse, // Cambiado de Fade a Collapse
  Tooltip,
  Badge
} from '@mui/material';
import {
  SportsScore as EventIcon,
  Group as ParticipantsIcon,
  Cached as RetryIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  FilterAlt as FilterActiveIcon,
  Sort as SortIcon,
  ArrowUpward as SortAscIcon,
  ArrowDownward as SortDescIcon,
  Clear as ClearIcon,
  KeyboardArrowRight as ArrowRightIcon
} from '@mui/icons-material';
import { useEventContext } from '../../context/EventContext';

export default function EventsTable ({ onEventSelect }) {
  const { competitionId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  
  // Breakpoints más granulares para mejor responsividad
  const isXXSmall = useMediaQuery('(max-width:480px)');
  const isXSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));
  const isMedium = useMediaQuery(theme.breakpoints.down('lg'));
  const isLarge = useMediaQuery(theme.breakpoints.down('xl'));
  
  const { evtData, pplData, loading, error, participantsResults, setResultStatus } = useEventContext();

  // Estados para filtros y búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  const [genderFilter, setGenderFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roundFilter, setRoundFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(!isXSmall);
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);

  // Estados de paginación y orden
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(isXSmall ? 8 : isSmall ? 15 : 25);
  const [orderBy, setOrderBy] = useState('eventNumber');
  const [order, setOrder] = useState('asc');

  // Función mejorada para obtener tamaños responsivos
  const getResponsiveSize = (base, type = 'font') => {
    const multipliers = {
      font: {
        xxsmall: 0.65,
        xsmall: 0.75,
        small: 0.85,
        medium: 0.95,
        large: 1,
        xlarge: 1.1
      },
      spacing: {
        xxsmall: 0.5,
        xsmall: 0.75,
        small: 1,
        medium: 1.25,
        large: 1.5,
        xlarge: 2
      },
      height: {
        xxsmall: 0.7,
        xsmall: 0.8,
        small: 0.9,
        medium: 1,
        large: 1.1,
        xlarge: 1.2
      }
    };

    let size = 'large';
    if (isXXSmall) size = 'xxsmall';
    else if (isXSmall) size = 'xsmall';
    else if (isSmall) size = 'small';
    else if (isMedium) size = 'medium';
    else if (isLarge) size = 'large';
    else size = 'xlarge';

    const multiplier = multipliers[type][size] || 1;
    return type === 'font' ? `${base * multiplier}rem` : base * multiplier;
  };

  // Detectar cambios en filtros activos
  useEffect(() => {
    let count = 0;
    if (searchTerm) count++;
    if (genderFilter !== 'all') count++;
    if (statusFilter !== 'all') count++;
    if (roundFilter !== 'all') count++;
    setActiveFiltersCount(count);
  }, [searchTerm, genderFilter, statusFilter, roundFilter]);

  // Actualizar rows per page cuando cambie el tamaño de pantalla
  useEffect(() => {
    if (isXXSmall) setRowsPerPage(5);
    else if (isXSmall) setRowsPerPage(8);
    else if (isSmall) setRowsPerPage(15);
    else setRowsPerPage(25);
    setPage(0);
  }, [isXXSmall, isXSmall, isSmall]);

  // Función para limpiar todos los filtros
  const clearAllFilters = () => {
    setSearchTerm('');
    setGenderFilter('all');
    setStatusFilter('all');
    setRoundFilter('all');
    setPage(0);
  };

  // Función de comparación para ordenar
  const descendingComparator = (a, b, prop) =>
    b[prop] < a[prop] ? -1 : b[prop] > a[prop] ? 1 : 0;

  const getComparator = (ord, prop) =>
    ord === 'desc'
      ? (a, b) => descendingComparator(a, b, prop)
      : (a, b) => -descendingComparator(a, b, prop);

  const stableSort = (arr, comp) => {
    const paired = arr.map((el, idx) => [el, idx]);
    paired.sort((a, b) => {
      const res = comp(a[0], b[0]);
      return res !== 0 ? res : a[1] - b[1];
    });
    return paired.map(pair => pair[0]);
  };

  // Función mejorada para cambiar el ordenamiento
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Mejorada función de detección de género de eventos
  const getGender = (eventName) => {
    if (!eventName) return 'Mixto';
    
    const normalizedEventName = removeAccents(eventName.toLowerCase());
    
    const maleKeywords = ['varonil', 'men', 'boys', 'masculino', 'male', 'hombres', 'niños'];
    const femaleKeywords = ['femenil', 'women', 'girls', 'femenino', 'female', 'mujeres', 'niñas'];
    
    const isMale = maleKeywords.some(keyword => normalizedEventName.includes(keyword));
    const isFemale = femaleKeywords.some(keyword => normalizedEventName.includes(keyword));
    
    if (isMale && !isFemale) {
      return 'Varonil';
    } else if (isFemale && !isMale) {
      return 'Femenil';
    } else {
      if (normalizedEventName.startsWith('men') || normalizedEventName.startsWith('boys')) {
        return 'Varonil';
      }
      if (normalizedEventName.startsWith('women') || normalizedEventName.startsWith('girls')) {
        return 'Femenil';
      }
      
      if (normalizedEventName.includes('men\'s') || normalizedEventName.includes('boys\'s')) {
        return 'Varonil';
      }
      if (normalizedEventName.includes('women\'s') || normalizedEventName.includes('girls\'s')) {
        return 'Femenil';
      }
      
      if (normalizedEventName.includes('9 year') || 
          normalizedEventName.includes('10 year') ||
          normalizedEventName.includes('11 year') ||
          normalizedEventName.includes('12 year')) {
        const firstWords = normalizedEventName.split(' ').slice(0, 2).join(' ');
        if (maleKeywords.some(k => firstWords.includes(k))) {
          return 'Varonil';
        }
        if (femaleKeywords.some(k => firstWords.includes(k))) {
          return 'Femenil';
        }
      }
      
      return 'Mixto';
    }
  };

  // Auxiliares de texto y género
  const removeAccents = str =>
    str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  const isCombinedEvent = name => {
    const keys = ['heptatlon','decatlon','triatlon','tetratlon','combined'];
    const n = removeAccents(name.toLowerCase());
    return keys.some(k => n.includes(k));
  };

  const resolveEventStatus = (eventKey, heatSet) => {
    // eventKey → "12-1"   (evento 12, ronda 1)
    const [evNum, rnd] = eventKey.split('-').map(Number);
  
    // build every heatKey that está en participantsResults
    const heatKeys = [...heatSet].map(h => `${evNum}_${rnd}_${h}`);
    const data     = heatKeys.map(k => participantsResults[k] || {});
  
    // ➊ aún no arrancan / en curso
    if (data.some(d => d.status === 'in_progress'))  return 'En curso';
    if (data.some(d => !d.status || d.status === 'not_started')) return 'Pendiente';
  
    // ➋ todos “finished” → miramos resultStatus
    const rs = data.map(d => d.resultStatus || 'unofficial');
    if (rs.includes('under_protest')) return 'Bajo protesta';
    if (rs.includes('unofficial'))    return 'No oficial';
    return 'Oficial';
  };
  
  /* ─── agrupación por evento ───────────────────────────────── */
  const processedData = useMemo(() => {
    if (!evtData) return [];
  
    const grouped = evtData.reduce((acc, ev) => {
      const key = `${ev.eventNumber}-${ev.round}`;
  
      if (!acc[key]) {
        acc[key] = {
          eventNumber      : ev.eventNumber,
          eventName        : ev.eventName,
          round            : ev.round,
          gender           : getGender(ev.eventName),
          heats            : new Set(),
          participantsCount: 0,
          isCombined       : isCombinedEvent(ev.eventName)
        };
      }
  
      acc[key].heats.add(ev.heat);
  
      acc[key].participantsCount = acc[key].isCombined
        ? (ev.participants?.length || 0)                // prueba combinada
        : (acc[key].participantsCount + (ev.participants?.length || 0));
  
      return acc;
    }, {});
  
    return Object.values(grouped).map(ev => ({
      ...ev,
      heatsCount: ev.heats.size,
      status    : resolveEventStatus(
                    `${ev.eventNumber}-${ev.round}`,
                    ev.heats
                  )
    }));
  }, [evtData, participantsResults]);

  // Obtener listas de valores únicos para los filtros
  const genderOptions = useMemo(() => {
    const genders = [...new Set(processedData.map(item => item.gender))];
    return ['all', ...genders.sort()];
  }, [processedData]);

  const roundOptions = useMemo(() => {
    const rounds = [...new Set(processedData.map(item => item.round))];
    return ['all', ...rounds.sort((a, b) => a - b)];
  }, [processedData]);

  const statusOptions = [
    'all','Pendiente','En curso','Bajo protesta','No oficial','Oficial'
  ];

  // Filtrar datos procesados
  const filteredData = useMemo(() => {
    return processedData.filter(item => {
      const matchesSearch = searchTerm === '' || 
        item.eventName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.eventNumber.toString().includes(searchTerm);
      
      const matchesGender = genderFilter === 'all' || item.gender === genderFilter;
      const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
      const matchesRound = roundFilter === 'all' || item.round === parseInt(roundFilter);
      
      return matchesSearch && matchesGender && matchesStatus && matchesRound;
    });
  }, [processedData, searchTerm, genderFilter, statusFilter, roundFilter]);

  // Ordenar y paginar
  const sortedData = useMemo(
    () => stableSort(filteredData, getComparator(order, orderBy)),
    [filteredData, order, orderBy]
  );

  // Al hacer click en una fila
  const handleRowClick = (eventNumber, round) => {
    if (onEventSelect) {
      return onEventSelect(eventNumber, round);
    }
    const insideFinish = pathname.includes('/integraciones/finishlynx');
    const adminPath = `/${competitionId}/integraciones/finishlynx/eventos-procesados/${eventNumber}/${round}`;
    const directPath = `/${competitionId}/event/${eventNumber}/${round}`;
    navigate(insideFinish ? adminPath : directPath);
  };

  // Estados de carga / error / vacío
  if (loading) {
    return (
      <Box sx={{ p: getResponsiveSize(2, 'spacing') }}>
        <LinearProgress sx={{ mb: getResponsiveSize(2, 'spacing') }}/>
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center', mt:4, gap:2 }}>
        <Alert
          severity="error"
          variant="filled"
          sx={{ borderRadius: 2, fontSize: getResponsiveSize(0.875) }}
          action={
            <IconButton color="inherit" size="small" onClick={() => window.location.reload()}>
              <RetryIcon fontSize="small"/>
            </IconButton>
          }
        >
          Error al cargar los eventos
        </Alert>
      </Box>
    );
  }
  
  if (!evtData.length || !pplData.length) {
    return (
      <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center', p: getResponsiveSize(4, 'spacing') }}>
        <Avatar sx={{ 
          bgcolor: theme.palette.primary.light, 
          mb: getResponsiveSize(2, 'spacing'), 
          width: getResponsiveSize(80, 'height'), 
          height: getResponsiveSize(80, 'height') 
        }}>
          <EventIcon sx={{ fontSize: getResponsiveSize(40, 'height'), color: theme.palette.primary.main }}/>
        </Avatar>
        <Typography variant="h6" color="textSecondary" gutterBottom sx={{ fontSize: getResponsiveSize(1.25) }}>
          No hay eventos disponibles
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: getResponsiveSize(0.875) }}>
          Sube los archivos .ppl y .evt para comenzar
        </Typography>
      </Box>
    );
  }

  // Definición de columnas ajustadas por tamaño de pantalla
  const columns = [
    { 
      id: 'eventNumber', 
      label: isXXSmall ? 'N°' : 'N° Evento', 
      minWidth: isXXSmall ? 40 : isXSmall ? 60 : 100,
      maxWidth: isXSmall ? 80 : 120,
      align: 'center',
      visible: true 
    },
    { 
      id: 'eventName', 
      label: isXXSmall ? 'Evento' : 'Nombre del Evento', 
      minWidth: isXXSmall ? 100 : isXSmall ? 120 : isMedium ? 200 : 250,
      maxWidth: isSmall ? 200 : 350,
      visible: true 
    },
    { 
      id: 'gender', 
      label: 'Rama', 
      minWidth: isXXSmall ? 50 : isXSmall ? 70 : 100,
      maxWidth: 120,
      align: 'center',
      visible: !isXXSmall 
    },
    { 
      id: 'round', 
      label: isXSmall ? 'R' : 'Ronda', 
      minWidth: isXXSmall ? 35 : isXSmall ? 40 : 80,
      maxWidth: 100,
      align: 'center',
      visible: true 
    },
    { 
      id: 'heatsCount', 
      label: isXSmall ? 'H' : 'Heats', 
      minWidth: isXXSmall ? 35 : isXSmall ? 40 : 80,
      maxWidth: 100,
      align: 'center',
      visible: !isXXSmall 
    },
    { 
      id: 'participantsCount', 
      label: isXXSmall ? 'P' : isXSmall ? 'Part.' : 'Participantes', 
      minWidth: isXXSmall ? 40 : isXSmall ? 60 : 120,
      maxWidth: 140,
      align: 'center',
      visible: true 
    },
    { 
      id: 'status', 
      label: isXSmall ? 'Est.' : 'Estatus', 
      minWidth: isXXSmall ? 60 : isXSmall ? 70 : 100,
      maxWidth: 130,
      align: 'center',
      visible: true 
    },
  ].filter(col => col.visible);

  const statusStyles = status => {
    switch(status) {
      case 'Pendiente':
        return {
          color: theme.palette.warning.main,
          bgcolor: theme.palette.warning.light,
          borderColor: theme.palette.warning.main
        };
      case 'No oficial':
        return {
          color: theme.palette.info.main,
          bgcolor: theme.palette.info.light,
          borderColor: theme.palette.info.main
        };
      case 'Oficial':
        return {
          color: theme.palette.success.main,
          bgcolor: theme.palette.success.light,
          borderColor: theme.palette.success.main
        };
        case 'En curso':
          return { color: theme.palette.info.main,
           bgcolor: theme.palette.info.light };
        case 'Bajo protesta':
          return { 
            color: theme.palette.error.main,
            bgcolor: theme.palette.error.light 
          };
      default:
        return {};
    }
  };

  const StatusChip = ({ value, onChange }) => {
    const stop = e => e.stopPropagation();   // <── centralizado
  
    return (
      <FormControl
        size="small"
        sx={{ minWidth: 140 }}
        onMouseDown={stop}   // evita que el mousedown burbujee
        onClick={stop}       // evita que el click burbujee
      >
        <Select
          value={value}
          onChange={e => onChange(e.target.value)}
          sx={{
            fontWeight: 700,
            '.MuiSelect-select': { py: '2px' },
            ...statusStyles(value)
          }}
          variant="outlined"
          MenuProps={{ onClick: stop }}   // por si haces click dentro del menú
        >
          {['Pendiente','En curso','Bajo protesta',
            'No oficial','Oficial'].map(s => (
            <MenuItem key={s} value={s}>{s}</MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  // Render de los filtros - USANDO COLLAPSE PARA EVITAR ESPACIO EN BLANCO
  const renderFilters = () => (
    <Collapse in={showFilters} timeout="auto" unmountOnExit>
      <Box sx={{ 
        p: getResponsiveSize(isXSmall ? 1 : 2, 'spacing'), 
        borderRadius: 2, 
        backgroundColor: theme.palette.background.paper,
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        mb: getResponsiveSize(2, 'spacing')
      }}>
        <Grid container spacing={getResponsiveSize(2, 'spacing')} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              variant="outlined"
              size={isXXSmall ? "small" : "small"}
              label="Buscar evento"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: getResponsiveSize(0.875)
                },
                '& .MuiInputLabel-root': {
                  fontSize: getResponsiveSize(0.875)
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: getResponsiveSize(20, 'height') }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          
          <Grid item xs={6} sm={6} md={3}>
            <FormControl fullWidth size={isXXSmall ? "small" : "small"}>
              <InputLabel sx={{ fontSize: getResponsiveSize(0.875) }}>Rama</InputLabel>
              <Select
                value={genderFilter}
                onChange={(e) => setGenderFilter(e.target.value)}
                label="Rama"
                sx={{
                  '& .MuiSelect-select': {
                    fontSize: getResponsiveSize(0.875)
                  }
                }}
              >
                <MenuItem value="all">Todas</MenuItem>
                {genderOptions.filter(option => option !== 'all').map(option => (
                  <MenuItem key={option} value={option} sx={{ fontSize: getResponsiveSize(0.875) }}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={6} sm={6} md={2}>
            <FormControl fullWidth size={isXXSmall ? "small" : "small"}>
              <InputLabel sx={{ fontSize: getResponsiveSize(0.875) }}>Ronda</InputLabel>
              <Select
                value={roundFilter}
                onChange={(e) => setRoundFilter(e.target.value)}
                label="Ronda"
                sx={{
                  '& .MuiSelect-select': {
                    fontSize: getResponsiveSize(0.875)
                  }
                }}
              >
                <MenuItem value="all">Todas</MenuItem>
                {roundOptions.filter(option => option !== 'all').map(option => (
                  <MenuItem key={option} value={option} sx={{ fontSize: getResponsiveSize(0.875) }}>
                    Ronda {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={6} sm={6} md={2}>
            <FormControl fullWidth size={isXXSmall ? "small" : "small"}>
              <InputLabel sx={{ fontSize: getResponsiveSize(0.875) }}>Estatus</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Estatus"
                sx={{
                  '& .MuiSelect-select': {
                    fontSize: getResponsiveSize(0.875)
                  }
                }}
              >
                <MenuItem value="all">Todos</MenuItem>
                {statusOptions.filter(option => option !== 'all').map(option => (
                  <MenuItem key={option} value={option} sx={{ fontSize: getResponsiveSize(0.875) }}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={6} sm={6} md={2}>
            <Button 
              fullWidth 
              variant="outlined" 
              color="secondary"
              onClick={clearAllFilters}
              disabled={activeFiltersCount === 0}
              startIcon={<ClearIcon sx={{ fontSize: getResponsiveSize(16, 'height') }} />}
              size={isXXSmall ? "small" : "medium"}
              sx={{
                fontSize: getResponsiveSize(0.875),
                padding: `${getResponsiveSize(8, 'spacing')}px ${getResponsiveSize(16, 'spacing')}px`
              }}
            >
              {isXXSmall ? 'Limpiar' : 'Limpiar'}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Collapse>
  );

  // Render de información sobre resultados
  const renderResultsInfo = () => (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center', 
      mb: getResponsiveSize(2, 'spacing'),
      flexWrap: 'wrap',
      gap: 1
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: getResponsiveSize(0.875) }}>
          {sortedData.length === 0 ? 'No hay resultados' : 
           `${Math.min(filteredData.length, 1 + page * rowsPerPage)}-${Math.min(filteredData.length, (page + 1) * rowsPerPage)} de ${filteredData.length}`}
        </Typography>
        {activeFiltersCount > 0 && (
          <Chip 
            size="small"
            label={`${activeFiltersCount} filtro${activeFiltersCount > 1 ? 's' : ''}`}
            color="primary"
            variant="outlined"
            sx={{ 
              fontSize: getResponsiveSize(0.75),
              height: getResponsiveSize(24, 'height')
            }}
          />
        )}
      </Box>
      
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Tooltip title={showFilters ? "Ocultar filtros" : "Mostrar filtros"}>
          <IconButton 
            onClick={() => setShowFilters(!showFilters)}
            color={activeFiltersCount > 0 ? "primary" : "default"}
            size={isXXSmall ? "small" : "medium"}
          >
            <Badge badgeContent={activeFiltersCount} color="primary">
              {activeFiltersCount > 0 ? 
                <FilterActiveIcon sx={{ fontSize: getResponsiveSize(20, 'height') }} /> : 
                <FilterIcon sx={{ fontSize: getResponsiveSize(20, 'height') }} />
              }
            </Badge>
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  // Vista de tarjetas para pantallas pequeñas
  if (isXSmall) {
    return (
      <Box sx={{ p: getResponsiveSize(1, 'spacing') }}>
        {renderFilters()}
        {renderResultsInfo()}
        
        <Stack spacing={getResponsiveSize(1.5, 'spacing')}>
          {sortedData.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: getResponsiveSize(4, 'spacing') }}>
              <Typography variant="body1" color="text.secondary" sx={{ fontSize: getResponsiveSize(1) }}>
                No se encontraron eventos con los filtros actuales
              </Typography>
              {activeFiltersCount > 0 && (
                <Button 
                  variant="text" 
                  color="primary"
                  onClick={clearAllFilters}
                  sx={{ mt: 1, fontSize: getResponsiveSize(0.875) }}
                >
                  Limpiar filtros
                </Button>
              )}
            </Box>
          ) : (
            sortedData
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map(ev => (
                <Card 
                  key={`${ev.eventNumber}-${ev.round}`}
                  sx={{ 
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    },
                    borderRadius: 2,
                    overflow: 'hidden',
                    position: 'relative'
                  }}
                  onClick={() => handleRowClick(ev.eventNumber, ev.round)}
                >
                  <Box 
                    sx={{ 
                      height: getResponsiveSize(4, 'height'), 
                      width: '100%', 
                      backgroundColor: statusStyles(ev.status).color,
                      position: 'absolute',
                      top: 0,
                      left: 0
                    }} 
                  />
                  <CardContent sx={{ p: getResponsiveSize(1.5, 'spacing') }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Chip 
                        label={ev.eventNumber} 
                        color="primary" 
                        size={isXXSmall ? "small" : "small"}
                        sx={{ fontSize: getResponsiveSize(0.75) }}
                        avatar={
                          <Avatar sx={{ 
                            backgroundColor: 'transparent !important',
                            width: getResponsiveSize(16, 'height'),
                            height: getResponsiveSize(16, 'height')
                          }}>
                            <EventIcon sx={{ fontSize: getResponsiveSize(12, 'height') }} />
                          </Avatar>
                        } 
                      />
                      <StatusChip
                        value={ev.status}
                        onChange={newVal => {
                          [...ev.heats].forEach(h => {
                            const dbVal = (newVal === 'Bajo protesta')
                              ? 'under_protest' : (newVal === 'Oficial' ? 'official' : 'unofficial');
                            setResultStatus(ev.eventNumber, ev.round, h, dbVal);
                          });
                        }}
                      />
                    </Box>
                    <Typography 
                      variant="subtitle1" 
                      noWrap 
                      sx={{ 
                        mb: 1,
                        fontSize: getResponsiveSize(1),
                        fontWeight: 500
                      }}
                    >
                      {ev.eventName}
                    </Typography>
                    <Divider sx={{ my: getResponsiveSize(1, 'spacing') }} />
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: getResponsiveSize(0.875) }}>
                          {ev.gender}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: getResponsiveSize(0.875) }}>
                          Ronda {ev.round}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: getResponsiveSize(0.875) }}>
                          {ev.heatsCount} {ev.heatsCount === 1 ? 'Heat' : 'Heats'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sx={{ textAlign: 'right' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                          <ParticipantsIcon 
                            sx={{ 
                              mr: 0.5, 
                              color: theme.palette.text.secondary,
                              fontSize: getResponsiveSize(16, 'height')
                            }} 
                          />
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: getResponsiveSize(0.875) }}>
                            {ev.participantsCount}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    <Button 
                      endIcon={<ArrowRightIcon sx={{ fontSize: getResponsiveSize(16, 'height') }} />} 
                      sx={{ 
                        mt: getResponsiveSize(1, 'spacing'), 
                        width: '100%',
                        fontSize: getResponsiveSize(0.875)
                      }}
                      size="small"
                      variant="text"
                    >
                      Ver detalles
                    </Button>
                  </CardContent>
                </Card>
              ))
          )}
          
          {sortedData.length > 0 && (
            <TablePagination
              component="div"
              count={sortedData.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={e => { setRowsPerPage(+e.target.value); setPage(0); }}
              rowsPerPageOptions={isXXSmall ? [5, 8] : [5, 8, 15]}
              labelRowsPerPage="Filas:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              sx={{
                '& .MuiTablePagination-toolbar': {
                  fontSize: getResponsiveSize(0.875)
                },
                '& .MuiTablePagination-selectLabel': {
                  fontSize: getResponsiveSize(0.875)
                },
                '& .MuiTablePagination-displayedRows': {
                  fontSize: getResponsiveSize(0.875)
                }
              }}
            />
          )}
        </Stack>
      </Box>
    );
  }

  // Vista de tabla para pantallas más grandes
  return (
    <Box sx={{ p: getResponsiveSize(2, 'spacing') }}>
      {renderFilters()}
      {renderResultsInfo()}
      
      {/* Indicador de scroll horizontal cuando sea necesario */}
      {sortedData.length > 0 && (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'flex-end',
          mb: 1,
          opacity: 0.6
        }}>
          <Typography variant="caption" sx={{ 
            fontSize: getResponsiveSize(0.75),
            color: theme.palette.text.secondary,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5
          }}>
            {isMedium && '← Desliza para ver más columnas →'}
          </Typography>
        </Box>
      )}
      
      <TableContainer 
        component={Paper} 
        sx={{ 
          border: `1px solid ${theme.palette.divider}`, 
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          mb: getResponsiveSize(2, 'spacing'),
          // Scroll horizontal para tabla en escritorio/tablet
          overflow: 'auto',
          maxWidth: '100%',
          '&::-webkit-scrollbar': {
            height: 8,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: theme.palette.grey[100],
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.grey[400],
            borderRadius: 4,
            '&:hover': {
              backgroundColor: theme.palette.grey[500],
            },
          },
          // Para Firefox
          scrollbarWidth: 'thin',
          scrollbarColor: `${theme.palette.grey[400]} ${theme.palette.grey[100]}`,
        }}
      >
        <Table 
          stickyHeader 
          size={isSmall ? 'small' : 'medium'}
          sx={{ 
            '& .MuiTableCell-root': {
              padding: `${getResponsiveSize(8, 'spacing')}px ${getResponsiveSize(16, 'spacing')}px`,
              fontSize: getResponsiveSize(0.875),
            },
            // Ancho mínimo que fuerza scroll horizontal cuando es necesario
            minWidth: isSmall ? 600 : isMedium ? 800 : 1000,
            // Asegurar que las columnas mantengan sus anchos mínimos
            tableLayout: 'auto',
          }}
        >
          <TableHead>
            <TableRow>
              {columns.map(col => (
                <TableCell
                  key={col.id}
                  variant="head"
                  align={col.align}
                  sx={{
                    minWidth: col.minWidth,
                    maxWidth: col.maxWidth,
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.common.white,
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    transition: 'background-color 0.2s ease',
                    position: 'relative',
                    fontSize: getResponsiveSize(0.875),
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                    }
                  }}
                  onClick={() => handleRequestSort(col.id)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: col.align === 'center' ? 'center' : 'flex-start' }}>
                    {col.label}
                    {orderBy === col.id && (
                      <Box component="span" sx={{ ml: 1, position: 'absolute', right: 8 }}>
                        {order === 'asc' ? 
                          <SortAscIcon sx={{ fontSize: getResponsiveSize(16, 'height') }} /> : 
                          <SortDescIcon sx={{ fontSize: getResponsiveSize(16, 'height') }} />
                        }
                      </Box>
                    )}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: getResponsiveSize(4, 'spacing') }}>
                  <Typography variant="body1" color="text.secondary" sx={{ fontSize: getResponsiveSize(1) }}>
                    No se encontraron eventos con los filtros actuales
                  </Typography>
                  {activeFiltersCount > 0 && (
                    <Button 
                      variant="text" 
                      color="primary"
                      onClick={clearAllFilters}
                      sx={{ mt: 1, fontSize: getResponsiveSize(0.875) }}
                    >
                      Limpiar filtros
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              sortedData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map(ev => (
                  <TableRow
                    key={`${ev.eventNumber}-${ev.round}`}
                    hover
                    sx={{ 
                      cursor: 'pointer',
                      transition: 'background-color 0.15s ease',
                      '&:hover': {
                        backgroundColor: `${theme.palette.action.hover} !important`,
                      }
                    }}
                    onClick={() => handleRowClick(ev.eventNumber, ev.round)}
                  >
                    {columns.map(col => {
                      const val = ev[col.id];
                      return (
                        <TableCell 
                          key={col.id} 
                          align={col.align}
                          sx={{
                            minWidth: col.minWidth,
                            maxWidth: col.maxWidth,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            borderLeft: col.id === 'status' ? `4px solid ${statusStyles(val).color}` : 'none',
                            fontSize: getResponsiveSize(0.875)
                          }}
                        >
                          {col.id === 'status' ? (
                          <StatusChip
                            value={val}
                            onChange={newVal => {
                              /* Propaga a TODOS los heats del evento */
                              [...ev.heats].forEach(h => {
                                const dbVal = (newVal === 'Bajo protesta')
                                  ? 'under_protest' : (newVal === 'Oficial' ? 'official' : 'unofficial');
                                setResultStatus(ev.eventNumber, ev.round, h, dbVal);
                              });
                            }}
                          />
                          ) : col.id === 'participantsCount' ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              <ParticipantsIcon sx={{ fontSize: getResponsiveSize(16, 'height') }} color="action"/> 
                              <Typography variant="body2" component="span" sx={{ fontSize: getResponsiveSize(0.875) }}>
                                {val}
                              </Typography>
                            </Box>
                          ) : col.id === 'eventNumber' ? (
                            <Chip 
                              label={val} 
                              color="primary" 
                              size="small"
                              sx={{ 
                                fontWeight: 'bold',
                                fontSize: getResponsiveSize(0.75),
                                height: getResponsiveSize(24, 'height'),
                                '& .MuiChip-avatar': { 
                                  backgroundColor: 'transparent',
                                  width: getResponsiveSize(16, 'height'),
                                  height: getResponsiveSize(16, 'height')
                                } 
                              }}
                              avatar={
                                <Avatar>
                                  <EventIcon sx={{ fontSize: getResponsiveSize(12, 'height') }} />
                                </Avatar>
                              }
                            />
                          ) : col.id === 'eventName' ? (
                            <Tooltip title={val} enterDelay={500}>
                              <Typography 
                                variant="body2"
                                noWrap 
                                sx={{ 
                                  fontWeight: 500,
                                  fontSize: getResponsiveSize(0.875)
                                }}
                              >
                                {val}
                              </Typography>
                            </Tooltip>
                          ) : col.id === 'gender' ? (
                            <Chip 
                              label={val} 
                              size="small"
                              variant="outlined"
                              color={val === 'Varonil' ? 'info' : val === 'Femenil' ? 'secondary' : 'default'}
                              sx={{ 
                                fontSize: getResponsiveSize(0.75),
                                height: getResponsiveSize(24, 'height')
                              }}
                            />
                          ) : col.id === 'round' ? (
                            <Typography variant="body2" component="span" sx={{ fontSize: getResponsiveSize(0.875) }}>
                              {val}
                            </Typography>
                          ) : col.id === 'heatsCount' ? (
                            <Chip 
                              label={`${val} ${val === 1 ? 'Heat' : 'Heats'}`} 
                              size="small" 
                              variant="outlined"
                              color="default"
                              sx={{ 
                                fontSize: getResponsiveSize(0.75),
                                height: getResponsiveSize(24, 'height')
                              }}
                            />
                          ) : (
                            <Typography variant="body2" component="span" sx={{ fontSize: getResponsiveSize(0.875) }}>
                              {val}
                            </Typography>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {sortedData.length > 0 && (
        <TablePagination
          component={Paper}
          count={sortedData.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(+e.target.value); setPage(0); }}
          rowsPerPageOptions={isSmall ? [15, 25, 50] : [25, 50, 75, 100]}
          labelRowsPerPage={isSmall ? "Filas:" : "Filas por página:"}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          sx={{ 
            borderRadius: 2, 
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            '& .MuiToolbar-root': {
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: isSmall ? 'center' : 'space-between',
              fontSize: getResponsiveSize(0.875)
            },
            '& .MuiTablePagination-toolbar': {
              fontSize: getResponsiveSize(0.875)
            },
            '& .MuiTablePagination-selectLabel': {
              fontSize: getResponsiveSize(0.875)
            },
            '& .MuiTablePagination-displayedRows': {
              fontSize: getResponsiveSize(0.875)
            }
          }}
        />
      )}
    </Box>
  );
};